import axios from "axios";

/**
 * Turn any thrown error into a flat list of user-facing messages.
 *
 * The backend Laravel services (commerce, healthcare, labiq, …) return request
 * validation failures as HTTP 422 with a body shaped:
 *   { message: string, errors: { field: string[] } }
 * This extractor flattens that field-error map so a form can surface the
 * server's per-field messages, falling back to the top-level message and then
 * a generic message. Promoted from the duplicated `handleErrors` helpers (see
 * SampleTab / the inventory edit drawers) so 422 handling stays consistent.
 */
export const extractValidationErrors = (error: unknown): string[] => {
	if (axios.isAxiosError(error) && error.response) {
		const data = error.response.data as
			| {
					errors?: Record<string, string[]>;
					message?: string;
					detail?: string | { msg?: string }[];
			  }
			| undefined;

		if (data?.errors) {
			return Object.entries(data.errors).flatMap(([, messages]) => messages as string[]);
		}

		if (data?.message) {
			return [data.message];
		}

		// FastAPI (xoe-agent) — appended AFTER the Laravel branches so their precedence is
		// unchanged. FastAPI answers `{ detail: string }` for a raised HTTPException and
		// `{ detail: [{ loc, msg, type }] }` for a Pydantic validation failure; it never
		// sends `errors` or `message`, so this only ever fires where the branches above
		// found nothing. Without it, xoe-agent's 422 text was invisible to the user.
		if (typeof data?.detail === "string" && data.detail) {
			return [data.detail];
		}

		if (Array.isArray(data?.detail)) {
			const messages = data.detail
				.map((item) => item?.msg)
				.filter((msg): msg is string => typeof msg === "string" && msg.length > 0);

			if (messages.length > 0) {
				return messages;
			}
		}

		return ["An unknown error occurred."];
	}

	if (error instanceof Error) {
		return [error.message || "An unknown error occurred."];
	}

	return ["An unknown error occurred."];
};
